import React, { useEffect, useRef, useState } from 'react';
import {
  confidenceBand,
  confidenceLabel,
  generateHeadline,
  Headline,
  RegionLens,
  starRating,
} from '../lib/socialPulse';

// Social Pulse — sidebar News Ticker with Regional Lens.
//
// Seeds SEED_COUNT headlines on mount (and whenever the lens changes),
// then appends a fresh one every TICK_MS. Cap the visible list at
// MAX_VISIBLE so the drawer doesn't grow unbounded on a long session.
//
// Styling leans "financial terminal": slate background, monospace type,
// green/red deltas, gold ≥80% CITIZEN VERIFIED badge, red <30% volatile
// flag, pulsing LIVE dot, and a LENS dropdown in the header. Each row is
// effectively a pledge card — category · stars · pct · delta · volume.

const TICK_MS     = 120_000;     // 2 minutes, per spec
const SEED_COUNT  = 3;
const MAX_VISIBLE = 8;

const LENS_ACCENT: Record<RegionLens, string> = {
  LOCAL:  '#7dd3fc',
  GLOBAL: '#fcd34d',
  ASIA:   '#f472b6',
  EUROPE: '#a78bfa',
};

// Verified / volatile / neutral drive the right-hand label + badge. Gold
// for verified matches the spec ask ("gold CITIZEN VERIFIED badge").
const BAND_ACCENT: Record<ReturnType<typeof confidenceBand>, string> = {
  verified: '#fcd34d',
  volatile: '#fca5a5',
  neutral:  '#64748b',
};

function seed(lens: RegionLens): Headline[] {
  const out: Headline[] = [];
  for (let i = 0; i < SEED_COUNT; i++) out.push(generateHeadline(lens));
  return out;
}

function StarStrip({ score }: { score: number }): React.JSX.Element {
  const filled = starRating(score);
  const accent = BAND_ACCENT[confidenceBand(score)];
  return (
    <span style={{ fontSize: 10, letterSpacing: '0.05em', color: accent }}>
      {'★'.repeat(filled)}
      <span style={{ color: '#1e293b' }}>{'★'.repeat(5 - filled)}</span>
    </span>
  );
}

function LivePulse(): React.JSX.Element {
  return (
    <span style={{
      display:       'inline-flex',
      alignItems:    'center',
      gap:           5,
      color:         '#86efac',
      fontSize:      8,
      letterSpacing: '0.24em',
      fontWeight:    700,
    }}>
      <span
        aria-hidden
        style={{
          display:         'inline-block',
          width:           6,
          height:          6,
          borderRadius:    '50%',
          backgroundColor: '#22c55e',
          boxShadow:       '0 0 6px rgba(34,197,94,0.9)',
          animation:       'spPulse 1.6s ease-in-out infinite',
        }}
      />
      LIVE
    </span>
  );
}

function VerifiedBadge(): React.JSX.Element {
  return (
    <span style={{
      display:         'inline-flex',
      alignItems:      'center',
      gap:             4,
      padding:         '1px 6px',
      borderRadius:    3,
      border:          '1px solid #fcd34d',
      backgroundColor: 'rgba(252,211,77,0.08)',
      color:           '#fcd34d',
      fontSize:        7,
      letterSpacing:   '0.22em',
      fontWeight:      800,
      textShadow:      '0 0 4px rgba(252,211,77,0.35)',
    }}>
      ✦ CITIZEN VERIFIED
    </span>
  );
}

interface Props {
  lens: RegionLens;
}

export default function SocialPulseTicker({ lens }: Props): React.JSX.Element {
  const [items, setItems] = useState<Headline[]>(() => seed(lens));
  const cancelled = useRef(false);

  // Fresh seed on lens change — swap the whole strip rather than mixing
  // regions. A trader flipping from Norwich to Asia expects a clean
  // board, not yesterday's Anglia Square story still riding at the top.
  useEffect(() => {
    setItems(seed(lens));
  }, [lens]);

  // Append one fresh headline on each tick. New rows prepend so the eye
  // always lands on the freshest signal. We re-read `lens` from the ref
  // each tick so a mid-session lens flip doesn't need to reset the
  // interval.
  const lensRef = useRef(lens);
  useEffect(() => { lensRef.current = lens; }, [lens]);

  useEffect(() => {
    cancelled.current = false;
    const tick = window.setInterval(() => {
      if (cancelled.current) return;
      // Visibility guard (§5 performance): don't prepend while the tab
      // is hidden. Browsers already throttle setInterval in background
      // tabs, but a returning user shouldn't be greeted with a stack of
      // stale "fresh" items queued during their coffee break.
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
      setItems(prev => [generateHeadline(lensRef.current), ...prev].slice(0, MAX_VISIBLE));
    }, TICK_MS);

    // When the tab becomes visible again after being hidden for a
    // while, add exactly one catch-up headline so the board feels
    // responsive — but no more, to keep the cadence honest.
    let lastCatchup = Date.now();
    function onVisibility(): void {
      if (document.visibilityState !== 'visible') return;
      const now = Date.now();
      if (now - lastCatchup < TICK_MS) return;
      lastCatchup = now;
      setItems(prev => [generateHeadline(lensRef.current), ...prev].slice(0, MAX_VISIBLE));
    }
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      cancelled.current = true;
      window.clearInterval(tick);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  return (
    <div style={{
      backgroundColor: '#0b1220',
      border:          '1px solid #333',
      borderRadius:    8,
      padding:         '14px 16px',
      fontFamily:      'monospace',
    }}>
      {/* Terminal-style keyframes scoped locally — no global CSS bleed. */}
      <style>{`
        @keyframes spPulse {
          0%   { opacity: 1;   transform: scale(1); }
          50%  { opacity: 0.3; transform: scale(0.85); }
          100% { opacity: 1;   transform: scale(1); }
        }
        @keyframes spFlash {
          0%   { background-color: rgba(134,239,172,0.10); }
          100% { background-color: transparent; }
        }
      `}</style>

      {/* Header row — title · LIVE pulse · current-lens indicator.
          The LENS control now lives in the terminal strip above; the
          ticker is a consumer of `lens`, not an owner. */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <p style={{ color: '#475569', fontSize: 9, letterSpacing: '0.18em', margin: 0 }}>
            SOCIAL PULSE · NEWSWIRE
          </p>
          <LivePulse />
        </div>
        <span style={{
          color:         LENS_ACCENT[lens],
          fontSize:      7,
          letterSpacing: '0.22em',
          fontWeight:    800,
          padding:       '1px 6px',
          border:        `1px solid ${LENS_ACCENT[lens]}`,
          borderRadius:  3,
        }}>
          {lens}
        </span>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {items.map(h => {
          const pct      = Math.round(h.score * 100);
          const band     = confidenceBand(h.score);
          const accent   = BAND_ACCENT[band];
          const label    = confidenceLabel(h.score);
          const deltaCol = h.deltaPct >= 0 ? '#86efac' : '#fca5a5';
          const deltaSym = h.deltaPct >= 0 ? '▲' : '▼';
          const borderCol =
            band === 'verified' ? '#78350f' :
            band === 'volatile' ? '#450a0a' :
            '#333';
          return (
            <div
              key={h.id}
              style={{
                backgroundColor: '#0f172a',
                border:          `1px solid ${borderCol}`,
                borderRadius:    6,
                padding:         '8px 10px',
                animation:       'spFlash 1.4s ease-out',
                boxShadow:       band === 'verified' ? '0 0 12px rgba(252,211,77,0.12)' : undefined,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 5, flexWrap: 'wrap' }}>
                {h.regions.map(region => (
                  <span key={region} style={{
                    color:         LENS_ACCENT[region],
                    fontSize:      7,
                    letterSpacing: '0.18em',
                    fontWeight:    800,
                    padding:       '1px 5px',
                    border:        `1px solid ${LENS_ACCENT[region]}`,
                    borderRadius:  3,
                  }}>
                    {region}
                  </span>
                ))}
                <StarStrip score={h.score} />
                <span style={{ flex: 1 }} />
                <span style={{ color: accent, fontSize: 11, fontWeight: 700 }}>
                  {pct}%
                </span>
              </div>

              <p style={{ color: '#e2e8f0', fontSize: 11, lineHeight: 1.35, margin: 0 }}>
                {h.text}
              </p>

              {/* Terminal-style metrics strip: volume + delta */}
              <div style={{
                display:        'flex',
                alignItems:     'center',
                gap:            8,
                marginTop:      6,
                paddingTop:     5,
                borderTop:      '1px dashed #333',
                fontSize:       8,
                letterSpacing:  '0.1em',
              }}>
                <span style={{ color: '#475569' }}>
                  VOL <span style={{ color: '#94a3b8' }}>{h.volumeOC.toLocaleString()} OC</span>
                </span>
                <span style={{ color: '#334155' }}>·</span>
                <span style={{ color: deltaCol, fontWeight: 700 }}>
                  {deltaSym} {Math.abs(h.deltaPct).toFixed(2)}%
                </span>
                <span style={{ flex: 1 }} />
                {band === 'verified' && <VerifiedBadge />}
                {band === 'volatile' && (
                  <span style={{
                    color:         '#fca5a5',
                    fontSize:      7,
                    letterSpacing: '0.22em',
                    fontWeight:    800,
                  }}>
                    {label}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <p style={{ color: '#1e293b', fontSize: 9, letterSpacing: '0.1em', margin: '12px 0 0', textAlign: 'center' }}>
        Verified citizens weigh heavier than anonymous noise.
      </p>
    </div>
  );
}
